/**
 * Map Component for FlowRide Frontend
 * Main map component using MapLibre GL for displaying routes, vehicles,
 * and user location with real-time updates and navigation features.
 */

import maplibregl from "maplibre-gl";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { rawGpsDataService } from "../../services/navigation/RawGpsDataService";
import { cleanCircularReferences } from "../../tools/CircularReferenceTools";

// Extend MapLibre Map interface to include our custom properties
declare module "maplibre-gl" {
    interface Map { "_cameraUnsubscribe"?: () => void }
}

import "maplibre-gl/dist/maplibre-gl.css";
import { APP_DIAGNOSTICS, FPS_LIMITATION, MAP_LIBRE } from "../../config/global.configuration";
import { useMapNavigation } from "../../hooks/MapNavigationHook";
import { useMapVehicleFollow } from "../../hooks/useMapVehicleFollow.hook";
import { AnimationManager } from "../../managers/AnimationManager";
import { animationOrchestrator } from "../../managers/AnimationOrchestrator";
import { MapRenderThrottler } from "../../managers/MapRenderThrottler";
import { PollingManager } from "../../managers/PollingManager";
import { type VehicleManager } from "../../managers/VehicleManager";
import { RealtimeCache } from "../../model/RealtimeCache";
import { ItineraryService } from "../../services/ItineraryService";
import { subscribeToPageVisibilityTransitions } from "../../services/lifecycle/PageLifecycleStore";
import {
    bindRealtimeMemoryProfiling,
    bindVehicleLayerForMemoryProfiling,
    logAppMemoryReport,
    registerAppMemoryDevHelper,
} from "../../tools/AppMemoryProfiler.tools";
import { convertGeocodeItemToSearchResultItem } from "../../tools/GeocodeTools";
import { GeocodeSource, GeocodeType, SearchResultItem } from "../../types/geocode-ui.types";
import { followingStateEqual } from "./followingStateEquality";
import { LineFollower, type FollowingState } from "./LineFollower";
import { registerMapImageFailureRecovery } from "./mapImageFailureRecovery.tools";
import { BIKE_CUSTOM_LAYER_IDS, BIKE_STATIONS_LAYER_ID, STOP_CUSTOM_LAYER_IDS, VEHICLE_CUSTOM_LAYER_ID } from "./mapLayerIds";
import { StopPopup, useStopMapLayers } from "./stops";
import { useRealtimeTransitPath } from "./transitPath/realtimeTransitPath";
import { VehicleCustomLayer } from "./VehicleCustomLayer";
import { areVehicleSelectionSnapshotsEqual } from "./vehicleSelection/vehicleSelection.tools";
import {
    isValidVehicleSelectionLngLat,
    type PopupContent,
    type RealtimeTransitPathSelection,
    type VehicleSelectionInfo,
} from "./vehicleSelection/vehicleSelection.types";

import NoSleepApp from "no-sleep-app";
import { useContextMenuSearchHistory } from "../../hooks/ContextMenuSearchHistoryHook";
import headingCone from "../../image/icons/floating/HeadingCone.png";
import userPoint from "../../image/icons/floating/UserPoint.png";
import navGuide from "../../image/icons/navigation/NavGuide.png";
import {
    drawnItineraryGeometrySlice,
    ephemeralStore,
    mapReadySlice,
    navigationSessionSlice,
    setMapInstance,
    useEphemeralSelector,
    useMapInstance,
} from "../../store/ephemeral";

import { BIKE_STATION_POPUP_CONFIG } from "../../config/bikeStationPopup.configuration";
import {
    buildPoiQueryBox,
    NATIVE_POI_LAYERS,
} from "../../config/nativePoiLayers.configuration";
import { usePersistenceReady } from "../../store/persistence";
import { getMapStyleUrl, getMapTileUrl, type MapStyleId } from "../../types/MapStyle";
import { NavigationMode } from "../../types/Navigation";
import CorrelationDebugButton from "../Debug/CorrelationDebugButton";
import FpsDebugOverlay from "../Debug/FpsDebugOverlay";
import MapDevDebugPill from "../Debug/MapDevDebugPill";
import MapDevDebugPillStack from "../Debug/MapDevDebugPillStack";
import OptionsDebugButton from "../Debug/OptionsDebugButton";
import ZoomDebugButtons from "../Debug/ZoomDebugButtons";
import { BikeStationPopup } from "./bikeStations/BikeStationPopup";
import { BikeStationPopupModern } from "./bikeStations/BikeStationPopupModern";
import { useBikeStationMapLayers } from "./bikeStations/useBikeStationMapLayers";
import { MapContextMenu } from "./MapContextMenu";
import { usePoiPopup } from "./poi/usePoiPopup";

/** NoSleep app instance to prevent device sleep during navigation */
const noSleep: NoSleepApp = new NoSleepApp();

interface MapComponentProps {

    /** Vehicle manager instance for real-time vehicle tracking */
    "vehicleManager": VehicleManager;

    /** Callback for setting departure location */
    "onSetDeparture"?: ( item: SearchResultItem ) => void;

    /** Callback for setting arrival location */
    "onSetArrival"?: ( item: SearchResultItem ) => void;

    /** Whether search inputs are merged (single field). Used for stop popup marginTop. */
    "isMerge"?: boolean;

    /**
     * Notified when vehicle selection info changes (map-domain snapshot for Dynamic Island bridge).
     */
    "onVehicleSelectionInfoChange"?: ( info: VehicleSelectionInfo | undefined ) => void;

    /** Active MapTiler base style */
    "mapStyleId": MapStyleId;

    /** Whether dark UI palette should be used */
    "isDarkUi": boolean;

    /**
     * Callback invoked by the dev-only "Boussole" pill (toggle compass debug overlay).
     * Kept optional so non-dev integrations can omit it entirely.
     */
    "onShowCompassDebug"?: () => void;

    /**
     * Dev-only callback to zoom the map in by one step. When provided
     * alongside `onZoomOut`, the dev-only +/- zoom buttons are
     * mounted inside the map container.
     */
    "onZoomIn"?: () => void;

    /**
     * Dev-only callback to zoom the map out by one step. See
     * `onZoomIn` for the coupling rules.
     */
    "onZoomOut"?: () => void;

    /**
     * Called when a vehicle is selected so the parent can clear the itinerary transit path
     * (equivalent to deselecting the highlighted leg).
     */
    "onClearItineraryTransitPath"?: () => void;

    /** Whether itinerary debug helpers are enabled in itinerary views. */
    "debugItineraries": boolean;

    /** Called when the debug itineraries switch changes. */
    "onDebugItinerariesChange": ( value: boolean ) => void;

    /**
     * Ref populated with {@link clearVehicleSelection} so the parent can deactivate the
     * realtime transit path when an itinerary leg is highlighted.
     */
    "clearVehicleSelectionRef"?: React.MutableRefObject<( () => void ) | null>;
}

/**
 * Adds all custom sources, layers, and images to the map.
 * Called on initial load AND after every style change via setStyle().
 */
const setupCustomLayers = (
    map: maplibregl.Map,
    styleId: MapStyleId,
    vehicleLayerRef: React.MutableRefObject<VehicleCustomLayer | undefined>,
    handleUserMarkerClick: ( map: maplibregl.Map ) => void,
): void => {
    const defaultCoords: [number, number] = [
        rawGpsDataService.getLastValue().longitude,
        rawGpsDataService.getLastValue().latitude,
    ];

    // Raster source + layer for navigation mode
    if ( map.getSource( "raster-tiles" ) === undefined ) {
        map.addSource( "raster-tiles", {
            "type":     "raster",
            "url":      getMapTileUrl( styleId ),
            "tileSize": 512,
        } );
    }

    if ( map.getLayer( "raster-layer" ) === undefined ) {
        map.addLayer( {
            "id":      "raster-layer",
            "type":    "raster",
            "source":  "raster-tiles",
            "minzoom": 0,
            "maxzoom": 22,
            "layout":  { "visibility": "none" },
        } );
    }

    // Source for user marker
    if ( map.getSource( "user-location" ) === undefined ) {
        map.addSource( "user-location", {
            "type": "geojson",
            "data": {
                "type":     "FeatureCollection",
                "features": [
                    {
                        "type":     "Feature",
                        "geometry": {
                            "type":        "Point",
                            "coordinates": defaultCoords,
                        },
                        "properties": {},
                    },
                ],
            },
        } );
    }

    // Source for detected line
    if ( map.getSource( "detected-line" ) === undefined ) {
        map.addSource( "detected-line", {
            "type": "geojson",
            "data": {
                "type":       "Feature",
                "properties": {},
                "geometry":   {
                    "type":        "LineString",
                    "coordinates": [],
                },
            },
        } );
    }

    // Layer for detected line
    if ( map.getLayer( "detected-line-layer" ) === undefined ) {
        map.addLayer( {
            "id":     "detected-line-layer",
            "type":   "line",
            "source": "detected-line",
            "layout": {
                "line-join":  "round",
                "line-cap":   "round",
                "visibility": "none",
            },
            "paint": {
                "line-color":     "#4882c5",
                "line-width":     6,
                "line-opacity":   0.8,
                "line-gap-width": 1,
            },
        } );
    }

    // Layer for standard user pointer
    if ( map.getLayer( "user-location-layer" ) === undefined ) {
        map.addLayer( {
            "id":     "user-location-layer",
            "type":   "symbol",
            "source": "user-location",
            "layout": {
                "icon-image":            "user-marker-icon",
                "icon-size":             0.15,
                "icon-allow-overlap":    true,
                "icon-ignore-placement": true,
                "symbol-z-order":        "viewport-y",
            },
        } );
    }

    // Re-attach click / hover handlers for user marker (calibration only when a feature is hit)
    map.on( "click", "user-location-layer", ( e ) => {
        if ( e.features !== undefined && e.features.length > 0 ) {
            handleUserMarkerClick( map );
        }
    } );
    map.on( "mouseenter", "user-location-layer", () => {
        map.getCanvas().style.cursor = "pointer";
    } );
    map.on( "mouseleave", "user-location-layer", () => {
        map.getCanvas().style.cursor = "";
    } );

    // Layer for navigation pointer
    if ( map.getLayer( "navigation-guide-layer" ) === undefined ) {
        map.addLayer( {
            "id":     "navigation-guide-layer",
            "type":   "symbol",
            "source": "user-location",
            "layout": {
                "icon-image":              "navigation-guide",
                "icon-size":               0.35,
                "icon-allow-overlap":      true,
                "icon-rotate":             [ "get", "bearing" ],
                "icon-rotation-alignment": "map",
                "visibility":              "none",
            },
            "paint": { "icon-opacity": 0.9 },
        } );
    }

    // Load marker images (idempotent via hasImage check)
    void map.
        loadImage( userPoint ).
        then( ( image ) => {
            if ( !map.hasImage( "user-marker-icon" ) ) {
                map.addImage( "user-marker-icon", image.data );
            }
        } );

    void map.
        loadImage( headingCone ).
        then( ( image ) => {
            if ( !map.hasImage( "heading-cone-icon" ) ) {
                map.addImage( "heading-cone-icon", image.data );
            }
        } );

    void map.
        loadImage( navGuide ).
        then( ( image ) => {
            if ( !map.hasImage( "navigation-guide" ) ) {
                map.addImage( "navigation-guide", image.data );
            }
        } );

    // Source for heading marker
    if ( map.getSource( "user-heading-marker" ) === undefined ) {
        map.addSource( "user-heading-marker", {
            "type": "geojson",
            "data": {
                "type":     "FeatureCollection",
                "features": [
                    {
                        "type":     "Feature",
                        "geometry": {
                            "type":        "Point",
                            "coordinates": defaultCoords,
                        },
                        "properties": { "bearing": 0 },
                    },
                ],
            },
        } );
    }

    // Layer for heading marker
    if ( map.getLayer( "user-heading-marker-layer" ) === undefined ) {
        map.addLayer( {
            "id":     "user-heading-marker-layer",
            "type":   "symbol",
            "source": "user-heading-marker",
            "layout": {
                "icon-image":              "heading-cone-icon",
                "icon-size":               1.2,
                "icon-allow-overlap":      true,
                "icon-rotate":             [ "*", [ "get", "bearing" ], -1 ],
                "icon-rotation-alignment": "map",
                "icon-offset":             [ 0, -15 ],
                "visibility":              "visible",
            },
            "paint": { "icon-opacity": 0.8 },
        } );
    }

    // Ensure cone is below user icon
    try {
        map.moveLayer( "user-heading-marker-layer", "user-location-layer" );
    } catch {
        // Layer may not exist yet
    }

    // Re-add vehicle custom layer if it exists but is not on the map
    if ( vehicleLayerRef.current !== undefined && map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) === undefined ) {
        map.addLayer( vehicleLayerRef.current );
    }

    // Place vehicle layer below user marker
    try {
        if ( vehicleLayerRef.current !== undefined && map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) !== undefined ) {
            map.moveLayer( vehicleLayerRef.current.id, "user-location-layer" );
        }
    } catch {
        // Layer may not exist yet
    }

    // Source for animated navigation marker
    if ( map.getSource( "navigation-animated" ) === undefined ) {
        map.addSource( "navigation-animated", {
            "type": "geojson",
            "data": {
                "type":     "FeatureCollection",
                "features": [
                    {
                        "type":     "Feature",
                        "geometry": {
                            "type":        "Point",
                            "coordinates": defaultCoords,
                        },
                        "properties": { "bearing": 0 },
                    },
                ],
            },
        } );
    }

    // Layer for animated navigation pointer
    if ( map.getLayer( "navigation-guide-animated-layer" ) === undefined ) {
        map.addLayer( {
            "id":     "navigation-guide-animated-layer",
            "type":   "symbol",
            "source": "navigation-animated",
            "layout": {
                "icon-image":              "navigation-guide",
                "icon-size":               0.35,
                "icon-allow-overlap":      true,
                "icon-rotate":             [ "*", [ "get", "bearing" ], -1 ],
                "icon-rotation-alignment": "map",
                "visibility":              "none",
            },
            "paint": { "icon-opacity": 0.9 },
        } );
    }

    // Hide old navigation-guide layer
    try {
        map.setLayoutProperty( "navigation-guide-layer", "visibility", "none" );
    } catch {
        // Ignore if layer doesn't exist
    }
};

/** Itinerary service instance for API calls */
const itineraryService = new ItineraryService();

/** Distance (m) before a vehicle starts turning toward the next segment */
/** Horizontal/vertical margin from map edges when clamping stop popup position. */
const POPUP_EDGE_MARGIN_PX = 10;

// Helper functions to get accuracy from GPS services
const getCurrentAccuracy = (): number => {
    const rawGps = rawGpsDataService.getLastValue();

    return rawGps?.accuracy ?? 0;
};


export const MapComponent: React.FC<MapComponentProps> = ( {
    vehicleManager,
    onSetDeparture,
    onSetArrival,
    isMerge = false,
    onVehicleSelectionInfoChange,
    mapStyleId,
    isDarkUi,
    onShowCompassDebug,
    onZoomIn,
    onZoomOut,
    onClearItineraryTransitPath,
    debugItineraries,
    onDebugItinerariesChange,
    clearVehicleSelectionRef,
} ) => {
    const mapContainer = useRef<HTMLDivElement | null>( null );
    const mapRef = useRef<maplibregl.Map | null>( null );
    const [ animationManager, setAnimationManager ] = useState<AnimationManager>();
    const animationManagerRef = useRef<AnimationManager | null>( null );
    const renderThrottlerRef = useRef<MapRenderThrottler | null>( null );
    const vehicleLayer = useRef<VehicleCustomLayer>();
    const mapStyleIdRef = useRef( mapStyleId );
    const styleChangeTokenRef = useRef( 0 );
    const [ isMapInitialized, setIsMapInitialized ] = useState( false );

    // Debug overlay state (dev only)
    const [ debugStats, setDebugStats ] = useState( {
        "renderFps":    0,
        "maxRenderFps": 0,
        "busFps":       0,
        "maxBusFps":    0,
        "zoomLevel":    0,
        "pitchLevel":   0,
    } );
    const [ showFpsDebugOverlay, setShowFpsDebugOverlay ] = useState<boolean>( false );

    const [ showStopsBboxDebug, setShowStopsBboxDebug ] = useState<boolean>( false );

    const [ showStopsShrinkDebug, setShowStopsShrinkDebug ] = useState<boolean>( false );

    const [ showBusShapesDebug, setShowBusShapesDebug ] = useState<boolean>( false );

    const [ useModernBikeStationPopup, setUseModernBikeStationPopup ] = useState<boolean>( BIKE_STATION_POPUP_CONFIG.useModernPopupByDefault );

    // Vehicle path overlay state (bus click → remaining route)
    const [ selectedVehicle, setSelectedVehicle ] = useState<RealtimeTransitPathSelection | null>( null );

    const [ vehicleSelectionData, setVehicleSelectionData ] = useState( {
        "lngLat":  [ 0, 0 ] as [number, number],
        "content": undefined as PopupContent | undefined,
    } );

    /** Synchronous vehicle position for the follow-camera rAF loop (avoids React render lag). */
    const vehicleLngLatRef = useRef<[number, number] | null>( null );

    const { setupStopLayers, setStopPreloadDebugVisibility, stopPopupProps } = useStopMapLayers( {
        isDarkUi,
        isMapInitialized,
    } );

    const { poiPopupProps, openPoiPopup } = usePoiPopup();

    const { setupBikeStationLayers, bikeStationPopupProps, bikeStationPopupKey } = useBikeStationMapLayers( {
        isDarkUi,
        isMapInitialized,
    } );

    useEffect( () => {
        mapStyleIdRef.current = mapStyleId;
    }, [ mapStyleId ] );

    useEffect( () => {
        animationManagerRef.current = animationManager ?? null;
    }, [ animationManager ] );

    const handleToggleFpsDebugOverlay = useCallback( (): void => {
        setShowFpsDebugOverlay( ( previousValue ) => !previousValue );
    }, [] );

    /** Dumps a full `[AppMemory]` report to DevTools (dev debug pill). */
    const handleMemoryDebugLog = useCallback( (): void => {
        logAppMemoryReport( {
            "context":      "manual",
            "skipThrottle": true,
        } );
    }, [] );

    /**
     * Toggles the compass debug overlay owned by the parent page.
     * Stays a no-op if no `onShowCompassDebug` callback was provided.
     */
    const handleShowCompassDebug = useCallback( (): void => {
        if ( onShowCompassDebug === undefined ) {
            return;
        }
        onShowCompassDebug();
    }, [ onShowCompassDebug ] );

    useEffect( () => {
        setStopPreloadDebugVisibility( {
            "bbox":   showStopsBboxDebug,
            "shrink": showStopsShrinkDebug,
        } );
    }, [ showStopsBboxDebug, showStopsShrinkDebug, setStopPreloadDebugVisibility ] );

    const handleShowStopsBboxDebugChange = useCallback( ( value: boolean ): void => {
        setShowStopsBboxDebug( value );
    }, [] );

    const handleShowStopsShrinkDebugChange = useCallback( ( value: boolean ): void => {
        setShowStopsShrinkDebug( value );
    }, [] );

    const handleShowBusShapesDebugChange = useCallback( ( value: boolean ): void => {
        const managerMap = vehicleManager.getMap?.() ?? undefined;
        if ( managerMap === undefined ) {
            console.warn( "[MapComponent] Map not initialized yet. Ignoring bus shapes toggle." );

            return;
        }

        setShowBusShapesDebug( value );

        if ( value ) {
            vehicleManager.visualizeTrips();
        } else {
            vehicleManager.clearTrips();
        }
    }, [ vehicleManager ] );

    const persistenceReady = usePersistenceReady();

    // Context menu state
    const [ contextMenu, setContextMenu ] = useState<{
        "visible": boolean;
        "x":       number;
        "y":       number;
        "lat":     number;
        "lon":     number;
        "label":   string;
        "loading": boolean;
    }>( {
        "visible": false,
        "x":       0,
        "y":       0,
        "lat":     0,
        "lon":     0,
        "label":   "",
        "loading": false,
    } );

    // Ref for measuring and clamping the menu within viewport
    const menuRef = useRef<HTMLDivElement | null>( null );

    // Function to load directly the last position from IndexedDB

    // Function to center the camera on a position

    // Keep menu inside viewport bounds when visible
    useEffect( () => {
        if ( !contextMenu.visible ) {
            return;
        }
        const el = menuRef.current;
        if ( el == null ) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const margin = 8;
        const maxX = Math.max( margin, window.innerWidth - rect.width - margin );
        const maxY = Math.max( margin, window.innerHeight - rect.height - margin );
        const newX = Math.max( margin, Math.min( contextMenu.x, maxX ) );
        const newY = Math.max( margin, Math.min( contextMenu.y, maxY ) );
        if ( newX !== contextMenu.x || newY !== contextMenu.y ) {
            setContextMenu( ( prev ) => ( {
                ...prev,
                "x": newX,
                "y": newY,
            } ) );
        }
    }, [ contextMenu.visible, contextMenu.x, contextMenu.y ] );

    const openContextMenu = useCallback( ( x: number, y: number, lat: number, lon: number ) => {
        setContextMenu( {
            "visible": true,
            x,
            y,
            lat,
            lon,
            "label":   "",
            "loading": true,
        } );

        // Fetch reverse label from geocoding service
        void itineraryService.
            reverseGeocode( lat, lon ).
            then( ( res ) => {
                const label = res?.[0]?.result ?? `${ lat.toFixed( 5 ) }, ${ lon.toFixed( 5 ) }`;
                setContextMenu( ( prev ) => ( {
                    ...prev,
                    label,
                    "loading": false,
                } ) );
            } ).
            catch( () => {
                setContextMenu( ( prev ) => ( {
                    ...prev,
                    "label":   `${ lat.toFixed( 5 ) }, ${ lon.toFixed( 5 ) }`,
                    "loading": false,
                } ) );
            } );
    }, [] );

    const closeContextMenu = useCallback( () => {
        setContextMenu( ( prev ) => ( {
            ...prev,
            "visible": false,
        } ) );
    }, [] );

    const mapInstance = useMapInstance();
    const navigationMode = useEphemeralSelector( navigationSessionSlice, ( session ) => session.navigationMode );
    const drawnItineraryGeometry = useEphemeralSelector( drawnItineraryGeometrySlice, ( geometry ) => geometry );
    const setMapReady = useCallback( ( ready: boolean ) => {
        ephemeralStore.setValue( mapReadySlice, ( previous ) => (
            previous.isMapReady === ready
                ? previous
                : {
                    ...previous,
                    "isMapReady": ready,
                }
        ) );
    }, [] );
    const setMap = useCallback( ( map: maplibregl.Map | undefined ) => {
        setMapInstance( map );
        if ( map === undefined ) {
            setMapReady( false );
        }
    }, [ setMapReady ] );

    // User interaction tracking for camera update suspension
    const [ isUserInteracting, setIsUserInteracting ] = useState<boolean>( false );
    const interactionResumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );

    /** Set synchronously on pointerdown so the nav animation skips cone updates before React re-renders. */
    const isInteractingRef = useRef<boolean>( false );

    // Derived state: determine if we're in navigation mode (FREE or CONSTRAINED)
    const isNavigating = useMemo( () => {
        return navigationMode === NavigationMode.CONSTRAINED_NAVIGATION;
    }, [ navigationMode ] );

    // Hook for cursor and camera management
    /* Only enable animation when not interacting (user can pan/zoom without interruption) */
    const { handleUserMarkerClick, navigationFps, maxNavigationFps } = useMapNavigation( {
        "handleAnimation": !isUserInteracting,
        isInteractingRef,
    } );

    const hasValidVehicleSelection = selectedVehicle !== null &&
      isValidVehicleSelectionLngLat( vehicleSelectionData.lngLat );

    const { bindVehicleFollowLayer } = useMapVehicleFollow( {
        vehicleLngLatRef,
        "hasValidSelection": hasValidVehicleSelection,
    } );

    const applyEffectiveRenderFps = useCallback( (): void => {
        const configuredBusMaxFps = vehicleLayer.current?.getConfiguredFps() ?? 0;
        const effectiveMaxRenderFps = Math.max(
            FPS_LIMITATION.mapLibreRender,
            configuredBusMaxFps,
            maxNavigationFps,
        );

        animationManagerRef.current?.setMaxRenderFps( effectiveMaxRenderFps );
        renderThrottlerRef.current?.setMaxFps( effectiveMaxRenderFps );

        if ( __IS_DEV__ ) {
            setDebugStats( ( prev ) => ( {
                ...prev,
                "maxRenderFps": effectiveMaxRenderFps,
                "maxBusFps":    configuredBusMaxFps,
            } ) );
        }
    }, [ maxNavigationFps ] );


    // Context menu search history integration
    const { handleSetDeparture, handleSetArrival } = useContextMenuSearchHistory( {
        onSetDeparture,
        onSetArrival,
    } );

    // Reference to LineFollower
    const lineFollowerRef = useRef<LineFollower | null>( null );

    const isNavigatingRef = useRef( isNavigating );

    // Following state handler
    const handleRouteLineFollowingChange = useCallback(
        ( state: FollowingState ) => {
            ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
                const current = previous.routeLineFollowing;
                if ( current !== null && followingStateEqual( current, state ) ) {
                    return previous;
                }

                return {
                    ...previous,
                    "routeLineFollowing": state,
                };
            } );
        },
        [],
    );


    // Update LineFollower when active itinerary changes
    useEffect( () => {
        const lineFollower = lineFollowerRef.current;
        if (
            lineFollower !== null &&
            drawnItineraryGeometry !== null &&
            drawnItineraryGeometry !== undefined
        ) {
            lineFollower.setDrawnItineraryGeometry( drawnItineraryGeometry );
        }
    }, [ drawnItineraryGeometry ] );

    // Reset when exiting navigation mode
    useEffect( () => {
        isNavigatingRef.current = isNavigating;
        if ( !isNavigating ) {
            if ( lineFollowerRef.current != null ) {
                lineFollowerRef.current.reset();
            }

            ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
                if ( previous.routeLineFollowing === null ) {
                    return previous;
                }

                return {
                    ...previous,
                    "routeLineFollowing": null,
                };
            } );

            // Hide navigation cursor layer
            if ( mapRef.current != null ) {
                const map = mapRef.current;
                if ( map.getLayer( "navigation-guide-animated-layer" ) != null ) {
                    map.setLayoutProperty( "navigation-guide-animated-layer", "visibility", "none" );
                }
            }

            noSleep.disable();
        } else {
            noSleep.enable();

            // Initialize LineFollower if not already done
            if ( mapRef.current != null && lineFollowerRef.current == null ) {
                lineFollowerRef.current = new LineFollower( mapRef.current, {
                    "detectionRadius":      50, // 50 meters
                    "maxOffLineDistance":   30, // 30 meters
                    "stickToLineThreshold": 15, // 15 meters
                } );

                lineFollowerRef.current.setDrawnItineraryGeometry( ephemeralStore.peek( drawnItineraryGeometrySlice ) );

                // S'abonner aux changements d'état
                lineFollowerRef.current.onStateChange( handleRouteLineFollowingChange );
            }
        }
    }, [ handleRouteLineFollowingChange, isNavigating ] );

    const handleUpdateVehicleSelection = useCallback(
        ( lngLat: [number, number], content?: PopupContent ): void => {
            vehicleLngLatRef.current = lngLat;
            setVehicleSelectionData( ( previous ) => {
                const next = {
                    lngLat,
                    content,
                };

                return areVehicleSelectionSnapshotsEqual( previous, next ) ? previous : next;
            } );
        },
        [],
    );

    const lastBridgedVehicleSelectionRef = useRef<VehicleSelectionInfo | undefined>( undefined );

    /** Bridges vehicle selection snapshots to Home / Dynamic Island (map-domain type only). */
    useEffect( () => {
        if ( onVehicleSelectionInfoChange === undefined ) {
            return;
        }

        let nextSelection: VehicleSelectionInfo | undefined;

        if (
            vehicleSelectionData.content === undefined ||
            !isValidVehicleSelectionLngLat( vehicleSelectionData.lngLat ) ||
            selectedVehicle === null
        ) {
            nextSelection = undefined;
        } else {
            nextSelection = {
                "content": vehicleSelectionData.content,
                "lngLat":  vehicleSelectionData.lngLat,
                "tripId":  selectedVehicle.tripId,
            };
        }

        const previousSelection = lastBridgedVehicleSelectionRef.current;

        if (
            previousSelection === undefined && nextSelection === undefined
        ) {
            return;
        }

        if (
            previousSelection !== undefined &&
            nextSelection !== undefined &&
            previousSelection.tripId === nextSelection.tripId &&
            areVehicleSelectionSnapshotsEqual( previousSelection, nextSelection )
        ) {
            return;
        }

        lastBridgedVehicleSelectionRef.current = nextSelection;
        onVehicleSelectionInfoChange( nextSelection );
    }, [
        onVehicleSelectionInfoChange,
        selectedVehicle,
        vehicleSelectionData,
    ] );

    /** Stops vehicle follow mode and clears React selection state (realtime transit path teardown). */
    const applyVehicleSelectionCleared = useCallback( (): void => {
        setSelectedVehicle( null );
        vehicleLngLatRef.current = null;
        setVehicleSelectionData( {
            "lngLat":  [ 0, 0 ],
            "content": undefined,
        } );
        ephemeralStore.setValue( navigationSessionSlice, ( previous ) => {
            if ( !previous.isFollowingVehicle ) {
                return previous;
            }

            return {
                ...previous,
                "isFollowingVehicle": false,
            };
        } );
    }, [] );

    /**
     * Fully deselects the active vehicle (layer + React state), equivalent to clicking away from it.
     */
    const clearVehicleSelection = useCallback( (): void => {
        vehicleLayer.current?.clearSelection();
        applyVehicleSelectionCleared();
    }, [ applyVehicleSelectionCleared ] );

    /** Handles vehicle selection changes from VehicleCustomLayer click events. */
    const handleVehicleSelectionChange = useCallback(
        ( snapshot: RealtimeTransitPathSelection | null ): void => {
            if ( snapshot === null ) {
                applyVehicleSelectionCleared();

                return;
            }

            onClearItineraryTransitPath?.();
            setSelectedVehicle( {
                "tripId": snapshot.tripId,
                "color":  snapshot.color,
            } );
        },
        [ applyVehicleSelectionCleared, onClearItineraryTransitPath ],
    );

    const handleVehicleSelectionChangeRef = useRef( handleVehicleSelectionChange );
    const setupStopLayersRef = useRef( setupStopLayers );

    /** Exposes {@link clearVehicleSelection} to Home for itinerary ↔ realtime transit path exclusivity. */
    useEffect( () => {
        handleVehicleSelectionChangeRef.current = handleVehicleSelectionChange;
        setupStopLayersRef.current = setupStopLayers;
    }, [ handleVehicleSelectionChange, setupStopLayers ] );

    useEffect( () => {
        if ( clearVehicleSelectionRef === undefined ) {
            return;
        }

        clearVehicleSelectionRef.current = clearVehicleSelection;

        return (): void => {
            clearVehicleSelectionRef.current = null;
        };
    }, [ clearVehicleSelection, clearVehicleSelectionRef ] );

    // Realtime polling system (cache-based approach via Rust microservice)
    const realtimeCache = useMemo<RealtimeCache>( () => {
        return new RealtimeCache();
    }, [] );
    const pollingManagerRef = useRef<PollingManager | null>( null );

    // Draw remaining bus route on the map when a vehicle is clicked
    useRealtimeTransitPath( {
        "map":        mapInstance ?? undefined,
        "isMapReady": isMapInitialized,
        selectedVehicle,
        realtimeCache,
        vehicleManager,
        isDarkUi,
    } );

    useEffect( () => {
        if ( document.visibilityState === "visible" ) {
            animationManager?.start();
        } else {
            animationManager?.stop();
        }
    }, [ animationManager ] );

    // Initialize PollingManager when map is ready
    useEffect( () => {
        if ( !isMapInitialized || !mapRef.current ) {
            return;
        }

        vehicleManager.setRealtimeCache( realtimeCache );

        bindRealtimeMemoryProfiling( {
            "cache":            realtimeCache,
            "getVehicleInputs": () => vehicleManager.getRealtimeMemoryProfilingInputs(),
        } );
        registerAppMemoryDevHelper();

        const polling = new PollingManager( realtimeCache );
        polling.setMap( mapRef.current );
        polling.setOnDataUpdate( () => {
            vehicleManager.onDataUpdate();
            vehicleLayer.current?.refreshData();
        } );
        pollingManagerRef.current = polling;

        void polling.init().then( () => {
            polling.start();
        } );

        return () => {
            polling.destroy();
            pollingManagerRef.current = null;
            bindRealtimeMemoryProfiling( null );
            bindVehicleLayerForMemoryProfiling( null );
        };
    }, [ isMapInitialized, realtimeCache, vehicleManager ] );

    // Memory snapshot after map pan/zoom settles (MapLibre tile cache correlates with UNACCOUNTED Δ).
    useEffect( () => {
        const map = mapRef.current;
        if ( !isMapInitialized || map === null ) {
            return;
        }

        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let lastIdleLogMs = 0;

        const scheduleIdleMemoryLog = (): void => {
            if ( debounceTimer !== null ) {
                clearTimeout( debounceTimer );
            }

            debounceTimer = setTimeout( () => {
                debounceTimer = null;
                const now = Date.now();
                if ( now - lastIdleLogMs < APP_DIAGNOSTICS.memoryIdleThrottleMs ) {
                    return;
                }

                lastIdleLogMs = now;
                logAppMemoryReport( { "context": "map-idle" } );
            }, APP_DIAGNOSTICS.memoryIdleDebounceMs );
        };

        const onViewportSettled = (): void => {
            scheduleIdleMemoryLog();
        };

        map.on( "moveend", onViewportSettled );
        map.on( "zoomend", onViewportSettled );

        return () => {
            if ( debounceTimer !== null ) {
                clearTimeout( debounceTimer );
            }

            map.off( "moveend", onViewportSettled );
            map.off( "zoomend", onViewportSettled );
        };
    }, [ isMapInitialized ] );

    // Update with new GPS positions
    useEffect( () => {
        if ( !isNavigating || lineFollowerRef.current == null ) {
            return;
        }

        rawGpsDataService.subscribe( ( data ) => {
            // console.log( "🎯 [MapComponent] GPS data:", data );
            if ( lineFollowerRef.current !== null ) {
                lineFollowerRef.current.updatePosition( {
                    "latitude":  data.latitude,
                    "longitude": data.longitude,
                    "accuracy":  getCurrentAccuracy() || 10,
                    "heading":   data.heading ?? 0,
                    "speed":     data.speed ?? 0,
                    "timestamp": Date.now(),
                } );
            }
        } );
    }, [ isNavigating, lineFollowerRef ] );

    // --- Initialisation de la carte ---
    useEffect( () => {
        if ( mapContainer.current == null ) {
            return;
        }
        if ( mapRef.current !== null ) {
            return;
        }

        if ( !persistenceReady ) {
            return;
        }

        const intervalHandler = setInterval( () => {
            // Wait for container to take correct dimensions
            if (
                mapContainer.current === null ||
                Math.abs( mapContainer.current.offsetWidth - window.innerWidth ) > 5
            ) {
                return;
            }
            clearInterval( intervalHandler );

            const map = new maplibregl.Map( {
                "container": mapContainer.current,
                "maxZoom":   22,
                "maxPitch":  80,
                "style":     getMapStyleUrl( mapStyleIdRef.current ),
                "center":    [ rawGpsDataService.getLastValue().longitude, rawGpsDataService.getLastValue().latitude ],
                "zoom":      15,

                "maxTileCacheZoomLevels":                MAP_LIBRE.maxTileCacheZoomLevels,
                "cancelPendingTileRequestsWhileZooming": MAP_LIBRE.cancelPendingTileRequestsWhileZooming,

                // @ts-expect-error librairie de type incomplète
                "antialias":          true,
                "doubleClickZoom":    false,
                "attributionControl": {
                    "compact":           true,
                    "customAttribution": _APP_VERSION_,
                },
            } );

            /*
             * console.log( "🗺️ [MapComponent] MapLibre instance created", {
             *     "container": mapContainer.current,
             *     "center":    [ rawGpsDataService.getLastValue().longitude, rawGpsDataService.getLastValue().latitude ], // Geneva default
             * } );
             */

            mapRef.current = map;
            setIsMapInitialized( true );
            registerMapImageFailureRecovery( map );

            // Set the map reference on the orchestrator for coordinated repaints
            animationOrchestrator.setMap( map );

            const renderThrottler = new MapRenderThrottler( Math.max( FPS_LIMITATION.mapLibreRender, maxNavigationFps ) );
            renderThrottler.attach( map );
            renderThrottler.setPaused( document.visibilityState !== "visible" );
            renderThrottlerRef.current = renderThrottler;
            vehicleManager.setMap( map );
            setMap( map );

            map.on( "error", ( e ) => {
                console.error( "🗺️ [MapComponent] Map error:", cleanCircularReferences( e ) );
            } );

            // Track camera zoom and pitch for debug overlay (dev only)
            if ( __IS_DEV__ ) {
                const updateCameraStats = () => {
                    const currentZoom = map.getZoom();
                    const currentPitch = map.getPitch();
                    setDebugStats( ( prev ) => ( {
                        ...prev,
                        "zoomLevel":  currentZoom,
                        "pitchLevel": currentPitch,
                    } ) );
                    applyEffectiveRenderFps();
                };

                updateCameraStats();
                map.on( "zoom", updateCameraStats );
                map.on( "zoomend", updateCameraStats );
                map.on( "pitch", updateCameraStats );
                map.on( "pitchend", updateCameraStats );
            }

            if ( map.isStyleLoaded() ) {
                setMapReady( true );
            }

            map.on( "load", () => {
                // console.log( "🗺️ [MapComponent] Map loaded successfully" );

                if ( map.isStyleLoaded() ) {
                    setMapReady( true );
                }

                // Add all custom sources, layers, and images
                setupCustomLayers( map, mapStyleIdRef.current, vehicleLayer, handleUserMarkerClick );

                vehicleLayer.current = new VehicleCustomLayer( vehicleManager, handleUpdateVehicleSelection );
                bindVehicleLayerForMemoryProfiling( vehicleLayer.current );
                vehicleLayer.current.setAdaptiveFpsConfig( FPS_LIMITATION.vehiclePositionUpdate );
                vehicleLayer.current.onVehicleSelectionChange = handleVehicleSelectionChange;
                bindVehicleFollowLayer( vehicleLayer.current );

                // Set up FPS callbacks for debug overlay (dev only)
                if ( __IS_DEV__ && vehicleLayer.current !== undefined ) {
                    vehicleLayer.current.setOnBusFpsUpdate( ( busFps: number ) => {
                        setDebugStats( ( prev ) => ( {
                            ...prev,
                            busFps,
                            "maxBusFps": vehicleLayer.current?.getConfiguredFps() ?? 0,
                        } ) );
                    } );

                    // Initialize maxBusFps
                    const currentLayer = vehicleLayer.current;
                    if ( currentLayer !== undefined ) {
                        setDebugStats( ( prev ) => ( {
                            ...prev,
                            "maxBusFps": currentLayer.getConfiguredFps(),
                        } ) );
                    }
                }

                map.addLayer( vehicleLayer.current );

                const animManager = new AnimationManager(
                    vehicleManager,
                    vehicleLayer.current,
                    Math.max( FPS_LIMITATION.mapLibreRender, maxNavigationFps ),
                );

                // Set up render FPS callback for debug overlay (dev only)
                if ( __IS_DEV__ ) {
                    animManager.setOnFpsUpdate( ( renderFps ) => {
                        setDebugStats( ( prev ) => ( {
                            ...prev,
                            renderFps,
                            "maxRenderFps": animManager.getMaxRenderFps(),
                        } ) );
                    } );

                    // Initialize maxRenderFps
                    setDebugStats( ( prev ) => ( {
                        ...prev,
                        "maxRenderFps": animManager.getMaxRenderFps(),
                    } ) );
                }

                setAnimationManager( animManager );
                animationManagerRef.current = animManager;
                applyEffectiveRenderFps();

                if ( document.visibilityState === "visible" ) {
                    animManager.start();
                }

                // Initialize LineFollower if in navigation mode
                if ( isNavigating && lineFollowerRef.current == null ) {
                    lineFollowerRef.current = new LineFollower( map, {
                        "detectionRadius":      50, // 50 mètres
                        "maxOffLineDistance":   30, // 30 mètres
                        "stickToLineThreshold": 15, // 15 mètres
                    } );

                    // S'abonner aux changements d'état
                    lineFollowerRef.current.onStateChange( handleRouteLineFollowingChange );
                }

                void setupStopLayers( map );
                setupBikeStationLayers( map );

                logAppMemoryReport( {
                    "context":      "map-style-loaded",
                    "skipThrottle": true,
                } );

                /*
                 * const layers = map.getStyle().layers;
                 * console.log( layers.map( ( l ) => l.id ) );
                 */

                return () => {
                    animManager.stop();
                    if (
                        vehicleLayer.current !== undefined &&
                        map.getLayer( vehicleLayer.current.id ) !== undefined
                    ) {
                        map.removeLayer( vehicleLayer.current.id );
                        vehicleLayer.current = undefined;
                    }

                    // Clean up LineFollower callbacks
                    if ( lineFollowerRef.current != null ) {
                        lineFollowerRef.current.offStateChange( handleRouteLineFollowingChange );
                    }

                    // Cleanup touch listeners and context menu handlers
                    if ( containerEl != null ) {
                        if ( onTouchStart != null ) {
                            containerEl.removeEventListener( "touchstart", onTouchStart );
                        }
                        if ( clearTouch != null ) {
                            containerEl.removeEventListener( "touchend", clearTouch );
                        }
                        if ( clearTouch != null ) {
                            containerEl.removeEventListener( "touchcancel", clearTouch );
                        }
                        if ( onTouchMove != null ) {
                            containerEl.removeEventListener( "touchmove", onTouchMove );
                        }
                    }
                    if ( onContextMenu != null ) {
                        map.off( "contextmenu", onContextMenu );
                    }
                };
            } );

            // Right-click context menu
            let onContextMenu:
                | ( ( e: maplibregl.MapMouseEvent & { "originalEvent": MouseEvent } ) => void ) |
                null = null;
            onContextMenu = ( e: maplibregl.MapMouseEvent & { "originalEvent": MouseEvent } ) => {
                const { lngLat, originalEvent } = e;
                openContextMenu( originalEvent.clientX, originalEvent.clientY, lngLat.lat, lngLat.lng );
            };
            map.on( "contextmenu", onContextMenu );
            map.on( "click", ( e ) => {
                const features = map.queryRenderedFeatures( e.point );
                console.log( features.map( ( f ) => ( {
                    "layer":      f.layer.id,
                    "properties": f.properties,
                } ) ) );
            } );


            map.on( "click", ( e ) => {
                const bikeStationFeatures = map.queryRenderedFeatures( e.point, { "layers": [ BIKE_STATIONS_LAYER_ID ] } );

                if ( bikeStationFeatures.length > 0 ) {
                    return;
                }

                const features = map.queryRenderedFeatures( buildPoiQueryBox( e.point ), { "layers": Object.keys( NATIVE_POI_LAYERS ) } );

                if ( features.length === 0 ) {
                    return;
                }

                const feature = features[0];
                const name    = String( feature.properties?.name ?? "" );

                openPoiPopup(
                    e.originalEvent.clientX,
                    e.originalEvent.clientY,
                    e.lngLat.lat,
                    e.lngLat.lng,
                    name,
                );
            } );

            map.on( "mousemove", ( e ) => {
                const features = map.queryRenderedFeatures( buildPoiQueryBox( e.point ), { "layers": Object.keys( NATIVE_POI_LAYERS ) } );
                map.getCanvas().style.cursor = features.length > 0 ? "pointer" : "";
            } );
            map.on( "click", ( e ) => {
                const features = map.queryRenderedFeatures( e.point );
                features.forEach( ( f ) => console.log( JSON.stringify( f.layer.id ) ) );
            } );

            // Long-press on touch
            let touchTimeout: number | null = null;
            let touchStartPos: {
                "x": number;
                "y": number;
            } | null = null;
            const LONG_PRESS_MS = 500;

            let onTouchStart: ( ( ev: TouchEvent ) => void ) | null = null;
            let onTouchMove: ( ( ev: TouchEvent ) => void ) | null = null;
            let clearTouch: ( () => void ) | null = null;

            const containerEl = mapContainer.current;
            const mapInst = mapRef.current;

            onTouchStart = ( ev: TouchEvent ) => {
                if ( containerEl == null || mapInst == null ) {
                    return;
                }

                // Only consider single-finger long-press for context menu
                if ( ev.touches.length !== 1 ) {
                    if ( touchTimeout !== null ) {
                        window.clearTimeout( touchTimeout );
                    }
                    touchTimeout = null;
                    touchStartPos = null;

                    return;
                }
                const touch = ev.touches[0];
                touchStartPos = {
                    "x": touch.clientX,
                    "y": touch.clientY,
                };
                touchTimeout = window.setTimeout( () => {
                    // Ensure still single touch when timer fires
                    if ( containerEl != null && mapInst != null && ev.touches.length === 1 ) {
                        const rect = containerEl.getBoundingClientRect();
                        const point = [ touch.clientX - rect.left, touch.clientY - rect.top ] as [
                            number,
                            number,
                        ];
                        const ll = mapInst.unproject( point );
                        openContextMenu( touch.clientX, touch.clientY, ll.lat, ll.lng );
                    }
                }, LONG_PRESS_MS );
            };
            onTouchMove = ( ev: TouchEvent ) => {
                // Cancel if more than one finger is on screen
                if ( ev.touches.length !== 1 ) {
                    if ( touchTimeout !== null ) {
                        window.clearTimeout( touchTimeout );
                    }
                    touchTimeout = null;
                    touchStartPos = null;

                    return;
                }
                if ( touchStartPos == null ) {
                    return;
                }
                const touch = ev.touches[0];
                const dx = Math.abs( touch.clientX - touchStartPos.x );
                const dy = Math.abs( touch.clientY - touchStartPos.y );
                if ( dx > 10 || dy > 10 ) {
                    if ( touchTimeout !== null ) {
                        window.clearTimeout( touchTimeout );
                    }
                    touchTimeout = null;
                }
            };
            clearTouch = () => {
                if ( touchTimeout !== null ) {
                    window.clearTimeout( touchTimeout );
                }
                touchTimeout = null;
                touchStartPos = null;
            };
            containerEl?.addEventListener( "touchstart", onTouchStart );
            containerEl?.addEventListener( "touchend", clearTouch );
            containerEl?.addEventListener( "touchcancel", clearTouch );
            containerEl?.addEventListener( "touchmove", onTouchMove );

            // No return here; cleanup is handled in outer effect cleanup below

            map.on( "moveend", () => {
                pollingManagerRef.current?.onViewportChange();
            } );

            // Track user interactions to suspend automatic camera updates
            const handleInteractionStart = () => {
                isInteractingRef.current = true;
                if ( interactionResumeTimeoutRef.current !== null ) {
                    clearTimeout( interactionResumeTimeoutRef.current );
                    interactionResumeTimeoutRef.current = null;
                }
                setIsUserInteracting( true );
            };

            const handleInteractionEnd = () => {
                const RESUME_DELAY_MS = 400;
                interactionResumeTimeoutRef.current = setTimeout( () => {
                    interactionResumeTimeoutRef.current = null;
                    isInteractingRef.current = false;
                    setIsUserInteracting( false );
                }, RESUME_DELAY_MS );
            };

            // Listen to all interaction events (pan, zoom, rotate, pitch)
            containerEl?.addEventListener( "pointerdown", handleInteractionStart );
            containerEl?.addEventListener( "pointerup", handleInteractionEnd );
        }, 50 );

        return () => {
            clearInterval( intervalHandler );
            if ( interactionResumeTimeoutRef.current !== null ) {
                clearTimeout( interactionResumeTimeoutRef.current );
                interactionResumeTimeoutRef.current = null;
            }

            // Clean up interaction listeners
            if ( mapRef.current?._cameraUnsubscribe != null ) {
                mapRef.current._cameraUnsubscribe();
            }

            animationManagerRef.current?.stop();
            renderThrottlerRef.current?.detach();
            renderThrottlerRef.current = null;
        };
    }, [ applyEffectiveRenderFps, handleRouteLineFollowingChange, handleUserMarkerClick, handleVehicleSelectionChange, persistenceReady, isNavigating, maxNavigationFps, openContextMenu, setMap, setMapReady, setupStopLayers, vehicleManager, openPoiPopup, handleUpdateVehicleSelection, bindVehicleFollowLayer, setupBikeStationLayers ] );

    /**
     * Pauses or resumes rendering, animation, and realtime polling when the page
     * leaves or returns to the screen.
     *
     * @param isVisible Whether the page is now on screen.
     */
    const applyPageVisibility = useCallback( ( isVisible: boolean ) => {
        const renderThrottler = renderThrottlerRef.current;
        if ( isVisible ) {
            renderThrottler?.setPaused( false );
            animationManagerRef.current?.start();
            vehicleManager.resetInterpolationState();
            pollingManagerRef.current?.resume();
            animationOrchestrator.requestRepaint();
        } else {
            renderThrottler?.setPaused( true );
            animationManagerRef.current?.stop();
            pollingManagerRef.current?.pause();
        }
    }, [ vehicleManager ] );

    /*
     * Driven by the lifecycle store rather than `visibilitychange` alone: Chrome
     * skips that event when thawing a frozen page on Android, which used to leave
     * the render throttler paused and the map visibly frozen.
     */
    useEffect( () => {
        return subscribeToPageVisibilityTransitions( applyPageVisibility );
    }, [ applyPageVisibility ] );

    const originalLayerVisibilityRef = useRef<Map<string, string>>( new Map() );

    const applyNavigationLayerVisibility = useCallback( ( map: maplibregl.Map, isStyleInitialLoad: boolean = false ): void => {
        // Layers that are not part of the base style and must not be toggled
        const customLayerIds = [
            "raster-layer",
            "user-location-layer",
            "detected-line-layer",
            "navigation-guide-layer",
            "user-heading-marker-layer",
            "navigation-guide-animated-layer",
            ...STOP_CUSTOM_LAYER_IDS,
            ...BIKE_CUSTOM_LAYER_IDS,
            ...vehicleLayer.current !== undefined ? [ vehicleLayer.current.id ] : [],
        ];
        const customLayerPrefixes = [
            "vehicle-line-layer-",
            "vehicle-point-layer-",
            "vehicle-arrow-layer-",
        ];

        const isCustomLayer = ( layerId: string ): boolean => {
            return (
                customLayerIds.includes( layerId ) ||
                customLayerPrefixes.some( ( prefix ) => layerId.startsWith( prefix ) )
            );
        };

        if ( isNavigating ) {
            // Navigation mode: show raster layer, hide vector base layers
            try {
                map.setLayoutProperty( "raster-layer", "visibility", "visible" );

                const style = map.getStyle();
                if ( style.layers !== undefined ) {
                    for ( const layer of style.layers ) {
                        if ( layer.id !== undefined && !isCustomLayer( layer.id ) ) {
                            try {
                                // Save original visibility before hiding, if not already saved
                                if ( !originalLayerVisibilityRef.current.has( layer.id ) ) {
                                    const currentVis = map.getLayoutProperty( layer.id, "visibility" ) as string | undefined;
                                    originalLayerVisibilityRef.current.set( layer.id, currentVis ?? "" );
                                }
                                map.setLayoutProperty( layer.id, "visibility", "none" );
                            } catch {
                                // Ignore if layer doesn't support visibility property
                            }
                        }
                    }
                }
            } catch( error ) {
                console.warn( "🗺️ [MapComponent] Error switching to raster layer:", error );
            }

            return;
        }

        // Exploration mode: hide raster layer, restore vector base layers
        try {
            map.setLayoutProperty( "raster-layer", "visibility", "none" );

            // If this is called right after style load, the layers are already at their default state.
            if ( isStyleInitialLoad ) {
                originalLayerVisibilityRef.current.clear();

                return;
            }

            const style = map.getStyle();
            if ( style.layers !== undefined ) {
                for ( const layer of style.layers ) {
                    if ( layer.id !== undefined && !isCustomLayer( layer.id ) ) {
                        try {
                            const originalVis = originalLayerVisibilityRef.current.get( layer.id );

                            /*
                             * If we saved it, restore it. If it was "", it means it used the style default.
                             * To reset to default, we can set it to "visible" or "none" based on what we saved,
                             * or just skip if we don't know. MapLibre defaults to 'visible' if undefined,
                             * except for layers that are explicitly 'none' in the style JSON.
                             */
                            if ( originalVis !== undefined ) {
                                map.setLayoutProperty( layer.id, "visibility", originalVis === "" ? "visible" : originalVis );
                            }
                        } catch {
                            // Ignore if layer doesn't support visibility property
                        }
                    }
                }
            }
        } catch( error ) {
            console.warn( "🗺️ [MapComponent] Error switching to vector layers:", error );
        }
    }, [ isNavigating ] );

    // Toggle between raster and vector layers based on navigation mode
    useEffect( () => {
        const map = mapRef.current;
        if ( map === null ) {
            return;
        }

        applyNavigationLayerVisibility( map );
    }, [ applyNavigationLayerVisibility ] );

    // Switch base map style when mapStyleId changes (skip the initial render)
    const initialStyleRef = useRef( true );
    useEffect( () => {
        const map = mapRef.current;
        if ( map === null || !isMapInitialized ) {
            return;
        }

        // Skip the very first render — the map was already created with this style
        if ( initialStyleRef.current ) {
            initialStyleRef.current = false;

            return;
        }

        const cameraSnapshot = {
            "center":  map.getCenter(),
            "zoom":    map.getZoom(),
            "bearing": map.getBearing(),
            "pitch":   map.getPitch(),
        };
        const styleChangeToken = styleChangeTokenRef.current + 1;
        styleChangeTokenRef.current = styleChangeToken;

        const onStyleLoaded = (): void => {
            // Ignore stale callbacks when styles are changed quickly.
            if ( styleChangeToken !== styleChangeTokenRef.current ) {
                return;
            }

            // Small timeout to ensure the style is fully processed and layers are settled
            setTimeout( () => {
                if ( styleChangeToken !== styleChangeTokenRef.current ) {
                    return;
                }

                // Re-create the vehicle layer to ensure fresh WebGL context usage
                if ( vehicleLayer.current !== undefined ) {
                    try {
                        if ( map.getLayer( vehicleLayer.current.id ) !== undefined ) {
                            map.removeLayer( vehicleLayer.current.id );
                        }
                    } catch {
                        /* ignore */
                    }
                }
                vehicleLayer.current = new VehicleCustomLayer( vehicleManager, handleUpdateVehicleSelection );
                bindVehicleLayerForMemoryProfiling( vehicleLayer.current );
                vehicleLayer.current.setAdaptiveFpsConfig( FPS_LIMITATION.vehiclePositionUpdate );
                vehicleLayer.current.onVehicleSelectionChange = ( snapshot ) => {
                    handleVehicleSelectionChangeRef.current( snapshot );
                };
                bindVehicleFollowLayer( vehicleLayer.current );

                // Clear vehicle path overlay when style changes (layer/source are destroyed)
                setSelectedVehicle( null );

                // Update AnimationManager with the new layer
                if ( animationManagerRef.current !== null && vehicleLayer.current !== undefined ) {
                    const am = new AnimationManager(
                        vehicleManager,
                        vehicleLayer.current,
                        Math.max( FPS_LIMITATION.mapLibreRender, maxNavigationFps ),
                    );
                    setAnimationManager( am );
                    animationManagerRef.current = am;
                    applyEffectiveRenderFps();
                    if ( document.visibilityState === "visible" ) {
                        am.start();
                    }
                }

                setupCustomLayers( map, mapStyleId, vehicleLayer, handleUserMarkerClick );
                if ( vehicleLayer.current !== undefined ) {
                    vehicleLayer.current.refreshData();
                }
                vehicleManager.reAddRouteLayers();
                applyNavigationLayerVisibility( map, true );

                void setupStopLayersRef.current( map );
                void setupStopLayers( map );
                setupBikeStationLayers( map );

                // Keep the exact same camera state as before style switch.
                map.jumpTo( cameraSnapshot );
                setMapReady( true );

                logAppMemoryReport( {
                    "context":      "map-style-loaded",
                    "skipThrottle": true,
                } );
            }, 100 );
        };

        void map.once( "style.load", onStyleLoaded );
        setMapReady( false );
        map.setStyle( getMapStyleUrl( mapStyleId ), { "diff": false } );
    }, [ applyEffectiveRenderFps, applyNavigationLayerVisibility, bindVehicleFollowLayer, handleUpdateVehicleSelection, handleUserMarkerClick, handleVehicleSelectionChange, isMapInitialized, mapStyleId, maxNavigationFps, setMapReady, setupBikeStationLayers, setupStopLayers, vehicleManager ] );

    return (
        <div className="relative w-full h-full overflow-hidden">
            <div
                className="w-full h-full"
                ref={mapContainer}
            />
            <CorrelationDebugButton isDarkUi={isDarkUi} />
            {__IS_DEV__ && (
                <MapDevDebugPillStack isDarkUi={isDarkUi}>
                    {showFpsDebugOverlay && (
                        <FpsDebugOverlay
                            isDarkUi={isDarkUi}
                            maxNavigationFps={maxNavigationFps}
                            navigationFps={navigationFps}
                            stats={debugStats}
                        />
                    )}
                    <MapDevDebugPill
                        ariaLabel="Toggle FPS debug overlay"
                        isDarkUi={isDarkUi}
                        label="FPS"
                        onClick={handleToggleFpsDebugOverlay}
                        title="Toggle FPS debug"
                    />
                    <MapDevDebugPill
                        ariaLabel="Log memory diagnostics to console"
                        isDarkUi={isDarkUi}
                        label="Memory"
                        onClick={handleMemoryDebugLog}
                        title="Log [AppMemory] snapshot to DevTools"
                    />
                    <OptionsDebugButton
                        debugItineraries={debugItineraries}
                        isDarkUi={isDarkUi}
                        onDebugItinerariesChange={onDebugItinerariesChange}
                        onShowBusShapesChange={handleShowBusShapesDebugChange}
                        onShowStopsBboxChange={handleShowStopsBboxDebugChange}
                        onShowStopsShrinkChange={handleShowStopsShrinkDebugChange}
                        onUseModernBikeStationPopupChange={setUseModernBikeStationPopup}
                        showBusShapes={showBusShapesDebug}
                        showStopsBbox={showStopsBboxDebug}
                        showStopsShrink={showStopsShrinkDebug}
                        useModernBikeStationPopup={useModernBikeStationPopup}
                    />
                    {onShowCompassDebug !== undefined && (
                        <MapDevDebugPill
                            ariaLabel="Toggle compass debug overlay"
                            isDarkUi={isDarkUi}
                            label="Boussole"
                            onClick={handleShowCompassDebug}
                            title="Toggle compass debug (dev only)"
                        />
                    )}
                </MapDevDebugPillStack>
            )}
            {/*
             * Dev-only map zoom controls. Only mounted when both
             * callbacks are provided (consumer opt-in) and when the
             * build flag denotes a dev build.
             */}
            {__IS_DEV__ && onZoomIn !== undefined && onZoomOut !== undefined && (
                <ZoomDebugButtons
                    isDarkUi={isDarkUi}
                    onZoomIn={onZoomIn}
                    onZoomOut={onZoomOut}
                />
            )}
            {/* Popups et overlays */}
            {mapInstance != null && stopPopupProps.visible && (
                <StopPopup
                    isDarkUi={isDarkUi}
                    key={stopPopupProps.stopId}
                    map={mapInstance}
                    margins={{
                        "marginTop":    isMerge ? 152 : 210,
                        "marginRight":  POPUP_EDGE_MARGIN_PX,
                        "marginBottom": 15,
                        "marginLeft":   POPUP_EDGE_MARGIN_PX,
                    }}
                    {...stopPopupProps}
                />
            )}
            {/* Context menu and backdrop */}
            <MapContextMenu
                buildItem={() => {
                    const address = contextMenu.label !== "" ? contextMenu.label : `${ contextMenu.lat.toFixed( 5 ) }, ${ contextMenu.lon.toFixed( 5 ) }`;
                    const geocodeItem = {
                        "type":     GeocodeType.ADDRESS,
                        "id":       `pelias-${ contextMenu.lat }-${ contextMenu.lon }`,
                        "result":   address,
                        "source":   GeocodeSource.PELIAS,
                        "location": {
                        "coordinates": {
                            "lat": contextMenu.lat,
                            "lon": contextMenu.lon,
                            },
                        },
                    };

                    return convertGeocodeItemToSearchResultItem( geocodeItem );
                }}
                isDarkUi={isDarkUi}
                label={contextMenu.label !== "" ? contextMenu.label : `${ contextMenu.lat.toFixed( 5 ) }, ${ contextMenu.lon.toFixed( 5 ) }`}
                loading={contextMenu.loading}
                onClose={closeContextMenu}
                onSetArrival={handleSetArrival}
                onSetDeparture={handleSetDeparture}
                visible={contextMenu.visible}
                x={contextMenu.x}
                y={contextMenu.y}
            />
            <MapContextMenu
                buildItem={() => {
                    const geocodeItem = {
                        "type":     GeocodeType.ADDRESS,
                        "id":       `poi-${ poiPopupProps.lngLat[1] }-${ poiPopupProps.lngLat[0] }`,
                        "result":   poiPopupProps.name,
                        "source":   GeocodeSource.PELIAS,
                        "location": {
                            "coordinates": {
                                "lat": poiPopupProps.lngLat[1],
                                "lon": poiPopupProps.lngLat[0],
                            },
                        },
                    };

                    return convertGeocodeItemToSearchResultItem( geocodeItem );
                }}
                isDarkUi={isDarkUi}
                label={poiPopupProps.name}
                onClose={poiPopupProps.onClose}
                onSetArrival={onSetArrival}
                onSetDeparture={onSetDeparture}
                visible={poiPopupProps.visible}
                x={poiPopupProps.x}
                y={poiPopupProps.y}
            />
            {useModernBikeStationPopup
                ? (
                    <BikeStationPopupModern
                        bikesAvailable={bikeStationPopupProps.bikesAvailable}
                        capacity={bikeStationPopupProps.capacity}
                        isDarkUi={isDarkUi}
                        key={bikeStationPopupKey}
                        lngLat={bikeStationPopupProps.lngLat}
                        map={mapInstance ?? null}
                        margins={BIKE_STATION_POPUP_CONFIG.margins}
                        name={bikeStationPopupProps.name}
                        onClose={bikeStationPopupProps.onClose}
                        onSetArrival={onSetArrival}
                        operator={bikeStationPopupProps.operator}
                        operatorName={bikeStationPopupProps.operatorName}
                        rentalUris={bikeStationPopupProps.rentalUris}
                        stationId={bikeStationPopupProps.stationId}
                        visible={bikeStationPopupProps.visible}
                    />
                )
                : (
                    <BikeStationPopup
                        isDarkUi={isDarkUi}
                        key={bikeStationPopupKey}
                        lngLat={bikeStationPopupProps.lngLat}
                        name={bikeStationPopupProps.name}
                        onClose={bikeStationPopupProps.onClose}
                        onSetArrival={onSetArrival}
                        operator={bikeStationPopupProps.operator}
                        operatorName={bikeStationPopupProps.operatorName}
                        rentalUris={bikeStationPopupProps.rentalUris}
                        stationId={bikeStationPopupProps.stationId}
                        visible={bikeStationPopupProps.visible}
                        x={bikeStationPopupProps.x}
                        y={bikeStationPopupProps.y}
                    />
                )}
        </div>
    );
};
